/**
 * Grabaciones de llamadas y reuniones: qué audio se admite, dónde se guarda
 * en el bucket privado `call-recordings` y cómo se lee el análisis del coach
 * (`coaching`) que guarda Claude desde la MCP del CRM.
 */
import type {
  CoachingFallo,
  CoachingLlamada,
  InteractionKind,
  InteractionOutcome,
  LeadInteraction,
} from "./types";

export const BUCKET_GRABACIONES = "call-recordings";
/** El límite del bucket: una reunión de hora y media en m4a cabe de sobra. */
export const MAX_BYTES_GRABACION = 200 * 1024 * 1024;

/** Extensión → tipo de contenido que acepta el bucket (todos `audio/*`). */
const TIPOS_DE_AUDIO: Record<string, string> = {
  m4a: "audio/mp4",
  mp4: "audio/mp4",
  mp3: "audio/mpeg",
  wav: "audio/wav",
  aac: "audio/aac",
  ogg: "audio/ogg",
  opus: "audio/ogg",
  webm: "audio/webm",
  amr: "audio/amr",
  "3gp": "audio/3gpp",
};

/** Para el `accept` del selector de archivo. */
export const ACEPTA_AUDIO = [
  "audio/*",
  ...Object.keys(TIPOS_DE_AUDIO).map((extension) => `.${extension}`),
].join(",");

export const TIPOS_GRABABLES: readonly InteractionKind[] = ["CALL", "MEETING"];

export function admiteGrabacion(interaccion: Pick<LeadInteraction, "kind">): boolean {
  return TIPOS_GRABABLES.includes(interaccion.kind);
}

/** Resultados que tienen sentido al subir una grabación (sin pedir más datos). */
export const RESULTADOS_GRABACION: readonly InteractionOutcome[] = [
  "ANSWERED",
  "NO_ANSWER",
  "PROPOSAL_SENT",
  "NOT_INTERESTED",
];

export interface AudioValido {
  extension: string;
  contentType: string;
}

/**
 * Comprueba el archivo y decide su tipo por la extensión: los móviles y los
 * navegadores no se ponen de acuerdo con el de un .m4a, .amr o .3gp, y el
 * bucket solo admite `audio/*`.
 */
export function validarAudio(archivo: { name: string; size: number }): AudioValido {
  const extension = archivo.name.includes(".")
    ? (archivo.name.split(".").pop() ?? "").toLowerCase()
    : "";
  const contentType = TIPOS_DE_AUDIO[extension];
  if (!contentType)
    throw new Error(
      "Ese archivo no es un audio admitido (m4a, mp3, wav, aac, ogg, webm, amr o 3gp)",
    );
  if (archivo.size > MAX_BYTES_GRABACION) throw new Error("La grabación pasa de 200 MB");
  if (archivo.size === 0) throw new Error("El archivo está vacío");
  return { extension, contentType };
}

export function rutaGrabacion(requestId: string, interactionId: string, extension: string): string {
  return `${requestId}/${interactionId}.${extension.replace(/^\./, "").toLowerCase()}`;
}

/** 754 → "12:34"; más de una hora sigue en minutos, como las marcas del coach. */
export function duracionLegible(segundos: number | null | undefined): string | null {
  if (segundos == null || !Number.isFinite(segundos) || segundos < 0) return null;
  const total = Math.round(segundos);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

export type EstadoGrabacion = "sin-grabacion" | "pendiente" | "analizada";

export function estadoGrabacion(
  interaccion: Pick<LeadInteraction, "recording_path" | "summary">,
): EstadoGrabacion {
  if (!interaccion.recording_path) return "sin-grabacion";
  return interaccion.summary ? "analizada" : "pendiente";
}

/* --- Análisis del coach ------------------------------------------------ */

function textos(valor: unknown): string[] {
  return Array.isArray(valor)
    ? valor.filter((v): v is string => typeof v === "string" && v.trim() !== "")
    : [];
}

function texto(valor: unknown): string | undefined {
  return typeof valor === "string" && valor.trim() ? valor.trim() : undefined;
}

function fallo(valor: unknown): CoachingFallo | null {
  if (!valor || typeof valor !== "object") return null;
  const f = valor as Record<string, unknown>;
  const quePaso = texto(f.que_paso);
  if (!quePaso) return null;
  return {
    minuto: texto(f.minuto) ?? "",
    que_paso: quePaso,
    frase_mejor: texto(f.frase_mejor) ?? "",
  };
}

/**
 * El jsonb tal y como venga, convertido en un análisis seguro de pintar: lo
 * que no siga el esquema se descarta en lugar de romper la ficha.
 */
export function leerCoaching(valor: unknown): CoachingLlamada | null {
  if (!valor || typeof valor !== "object" || Array.isArray(valor)) return null;
  const c = valor as Record<string, unknown>;
  const datos = (c.datos && typeof c.datos === "object" ? c.datos : {}) as Record<string, unknown>;
  const puntuacion =
    typeof c.puntuacion === "number" && c.puntuacion >= 1 && c.puntuacion <= 10
      ? Math.round(c.puntuacion)
      : 0;
  return {
    datos: {
      fecha_boda: texto(datos.fecha_boda),
      lugar: texto(datos.lugar),
      invitados: typeof datos.invitados === "number" ? datos.invitados : undefined,
      deseos: textos(datos.deseos),
      miedos: textos(datos.miedos),
      objeciones: textos(datos.objeciones),
      presupuesto: texto(datos.presupuesto),
    },
    proxima_accion: texto(c.proxima_accion) ?? "",
    bien: textos(c.bien),
    fallos: Array.isArray(c.fallos)
      ? c.fallos.map(fallo).filter((f): f is CoachingFallo => f !== null)
      : [],
    puntuacion,
    fase: c.fase === "reunion" ? "reunion" : "setting",
  };
}

export type TonoDato = "neutro" | "deseo" | "miedo" | "objecion";

export interface ChipDato {
  etiqueta: string;
  valor: string;
  tono: TonoDato;
}

/** Los datos que contó la pareja, en el orden en que se pintan como chips. */
export function chipsDatos(coaching: CoachingLlamada): ChipDato[] {
  const { datos } = coaching;
  const chips: ChipDato[] = [];
  if (datos.fecha_boda) chips.push({ etiqueta: "Fecha", valor: datos.fecha_boda, tono: "neutro" });
  if (datos.lugar) chips.push({ etiqueta: "Lugar", valor: datos.lugar, tono: "neutro" });
  if (datos.invitados != null)
    chips.push({ etiqueta: "Invitados", valor: String(datos.invitados), tono: "neutro" });
  if (datos.presupuesto)
    chips.push({ etiqueta: "Presupuesto", valor: datos.presupuesto, tono: "neutro" });
  for (const deseo of datos.deseos ?? [])
    chips.push({ etiqueta: "Quieren", valor: deseo, tono: "deseo" });
  for (const miedo of datos.miedos ?? [])
    chips.push({ etiqueta: "Temen", valor: miedo, tono: "miedo" });
  for (const objecion of datos.objeciones ?? [])
    chips.push({ etiqueta: "Objeción", valor: objecion, tono: "objecion" });
  return chips;
}

export const ETIQUETA_FASE: Record<CoachingLlamada["fase"], string> = {
  setting: "Llamada de setting",
  reunion: "Reunión de venta",
};

export function textoPendientes(n: number): string {
  return n === 1
    ? "1 llamada grabada pendiente de analizar"
    : `${n} llamadas grabadas pendientes de analizar`;
}
